import React from 'react';
import type {PatternDetectResult} from '../../../../types/patternRecognition';
import type {AIAnalysis, PatternCode, SignalDescription} from '../types';
import {PATTERN_DESCRIPTIONS} from '../config/signalDescriptions';
import {
    calcKlineGap,
    formatRelativeTime,
    formatSignalTime,
    formatTimeTooltip,
    getSignalBorderClass,
    parseCreatedAt
} from '../utils/patternTime';
import {AiAnalysisReport} from './AiAnalysisReport';
import {SignalDescriptionBlock} from './SignalDescriptionBlock';

interface PatternSignalCardProps {
    pattern: PatternDetectResult;
    selected: boolean;
    onSelect: () => void;
    onViewChart: () => void;
    viewChartActive: boolean;
    interval?: string;
    aiData?: AIAnalysis;
    aiLoading: boolean;
    aiError?: string | null;
    isExpanded: boolean;
    onAiClick: () => void;
    quickTradeEnabled: boolean;
    canUseAiAnalysis: boolean;
    isPocPage: boolean;
    validationError?: string | null;
    expiryReason?: string | null;
    onGoTrade?: (direction: 'FORWARD' | 'REVERSE') => void;
}

function directionLabel(dir: number): string {
    if (1 === dir) return '看多';
    if (-1 === dir) return '看空';
    return '中性';
}

function directionClass(dir: number): string {
    if (1 === dir) return 'bull';
    if (-1 === dir) return 'bear';
    return 'neutral';
}

export const PatternSignalCard: React.FC<PatternSignalCardProps> = ({
                                                                        pattern,
                                                                        selected,
                                                                        onSelect,
                                                                        onViewChart,
                                                                        viewChartActive,
                                                                        interval,
                                                                        aiData,
                                                                        aiLoading,
                                                                        aiError,
                                                                        isExpanded,
                                                                        onAiClick,
                                                                        quickTradeEnabled,
                                                                        canUseAiAnalysis,
                                                                        isPocPage,
                                                                        validationError,
                                                                        expiryReason,
                                                                        onGoTrade,
                                                                    }) => {
    const desc: SignalDescription | undefined = PATTERN_DESCRIPTIONS[pattern.patternCode as PatternCode];
    const hasData = !!aiData;
    const recognizedAt = parseCreatedAt(pattern.createdAt);
    const timeGap = calcKlineGap(pattern.klineEndTime, Date.now(), interval);
    const borderClass = getSignalBorderClass(timeGap);

    return (
        <div
            className={`pattern-card${selected ? ' selected' : ''}${-1 === pattern.direction ? ' bear-card' : ''}${borderClass ? ' ' + borderClass : ''}`}
            onClick={onSelect}
        >
            <div className="pc-header">
                <div className="pc-name">{pattern.patternName}</div>
                <div className={`pc-dir ${directionClass(pattern.direction)}`}>
                    {directionLabel(pattern.direction)}
                </div>
            </div>
            <div className="pc-meta">
                <span>{interval ?? '1H'}</span>
                <div className="pc-conf">
                    <span>置信度</span>
                    <div className="conf-bar">
                        <div
                            className={`conf-fill ${directionClass(pattern.direction)}`}
                            style={{width: `${pattern.confidence}%`}}
                        />
                    </div>
                    <span style={{color: `var(--pt-${directionClass(pattern.direction)})`}}>
                        {pattern.confidence}%
                    </span>
                </div>
            </div>
            <div className="pc-time">
                <div>形成于 {formatSignalTime(pattern.klineEndTime)}</div>
                <div style={{fontSize: 10, color: 'var(--pt-text-dim)'}}
                     title={null == recognizedAt ? undefined : formatTimeTooltip(recognizedAt)}>
                    识别于 {null == recognizedAt ? '--' : formatSignalTime(recognizedAt)}
                    {null == recognizedAt ? '' : ` · ${formatRelativeTime(recognizedAt)}`}
                </div>
            </div>
            {desc && <SignalDescriptionBlock description={desc}/>}
            <button
                type="button"
                className={`pc-view-chart-btn${viewChartActive ? ' active' : ''}`}
                onClick={(event) => {
                    event.stopPropagation();
                    onViewChart();
                }}
            >
                在图表中查看
            </button>
            {!isPocPage || hasData || pattern.hasAiAnalysis ? (
                <div className="pc-ai-section">
                    <button
                        className={`pc-ai-btn${aiLoading ? ' loading' : ''}${hasData || pattern.hasAiAnalysis ? ' has-data' : ''}`}
                        onClick={(e) => {
                            e.stopPropagation();
                            onAiClick();
                        }}
                        disabled={aiLoading || !canUseAiAnalysis}
                        title={aiLoading ? 'AI 分析中...' : hasData ? '查看AI解读' : '触发AI分析'}
                    >
                        {aiLoading ? '⏳' : hasData ? '📋' : '🤖'}
                    </button>
                </div>
            ) : null}
            {isExpanded && aiData && (
                <AiAnalysisReport
                    aiData={aiData}
                    holdingPeriodText={aiData.holdingPeriodText}
                    onGoTrade={onGoTrade}
                    quickTradeEnabled={quickTradeEnabled}
                    validationError={validationError}
                    expiryReason={expiryReason}
                    signalDirection={pattern.direction as 1 | -1 | 0}
                    signalConfidence={pattern.confidence}
                />
            )}
            {isExpanded && aiLoading && <div className="pc-ai-report">AI 分析中...</div>}
            {isExpanded && !aiLoading && aiError && <div className="ai-qt-error">{aiError}</div>}
        </div>
    );
};
